import React, { useState, useEffect } from "react";
import { C, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, RequestsTab, useToast, exportToCSV, ImportModal
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import { openCustodyCount, mapCustodyRow } from "./AssetsPage";
import {
  getEmployees, createEmployee, updateEmployee, deleteEmployee,
  getLeaves, createLeave, updateLeave, deleteLeave,
  getTransactions, createTransaction,
  getAssetsCustody,
  getAllowances, createAllowance,
} from "./supabaseClient";
import {
  Briefcase, Users, UserPlus, Plus, Search, CheckCircle, Clock,
  FileText, Calendar, Target,
  TrendingUp, Building,
  DollarSign, CalendarOff, Layers,
  UserCheck, CalendarDays, TreePine, Inbox,
  AlertTriangle, Package, Download, Upload
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ HR Page
   ═════════════════════════════════════════════ */
/* ✦ محوّل صف employees (snake_case من DB) → camelCase الذي يقرأه العرض. */
function mapEmployeeRow(r) {
  return {
    id: r.id,
    empId: r.emp_id,
    name: r.name,
    department: r.department,
    position: r.position,
    salary: Number(r.salary) || 0,
    phone: r.phone || "",
    email: r.email || "",
    joinDate: r.join_date || "",
    qualification: r.qualification || "—",
    nationality: r.nationality || "—",
    leaveBalance: r.leave_balance ?? 0,
    status: r.status || "active",
    notes: r.notes || "",
  };
}

/* ✦ طبقة تحويل: صف transactions (module=hr, direction=expense) → شكل بطاقة الصرف.
   الشهر/السنة الهجريان مخزّنان في description بصيغة "رواتب <شهر> <سنة>" ونستعيدهما بالـ regex. */
function mapTxnToDisbursement(t) {
  const desc = t.description || "";
  const m = desc.match(/^رواتب\s+(.+?)\s+(\d{3,4})$/);
  return {
    id: t.id,
    month: m ? m[1] : (desc || "—"),
    year: m ? m[2] : "",
    totalAmount: Number(t.amount) || 0,
    paymentMethod: t.payment_method || "—",
    disbursementDate: t.txn_date || "",
    notes: t.notes || "",
  };
}

export default function HRPage({ user }) {
  const [tab, setTab] = useState("employees");
  const [triggers, setTriggers] = useState({ leave: 0 });
  const perms = getPerms(user);

  // ✦ الحالة الحيّة مرفوعة للأب (مطلوبة في reportSources + الـ tabs)
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(true);
  useEffect(() => { getEmployees().then(d => { setEmployees((d || []).map(mapEmployeeRow)); setLoadingEmp(false); }).catch(() => setLoadingEmp(false)); }, []);

  const [leaves, setLeaves] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(true);
  useEffect(() => { getLeaves().then(d => { setLeaves(d || []); setLoadingLeaves(false); }).catch(() => setLoadingLeaves(false)); }, []);

  const [disbursements, setDisbursements] = useState([]);
  useEffect(() => { getTransactions("hr", "expense").then(all => setDisbursements((all || []).map(mapTxnToDisbursement))).catch(() => {}); }, []);

  const tabs = [
    { key: "employees", label: "الموظفون", icon: Users },
    { key: "structure", label: "الهيكل التنظيمي", icon: Layers },
    { key: "leaves", label: "الإجازات", icon: TreePine },
    { key: "requests", label: "الطلبات", icon: Inbox },
    { key: "payroll", label: "الرواتب", icon: DollarSign },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  const money = (v) => (typeof v === "number" ? v.toLocaleString("ar-SA") : (v ?? "—"));
  // ✦ صفوف الإجازات محوّلة لشكل الأعمدة (اسم الموظف من employees state، start_date→from ...)
  const leaveReportRows = leaves.map(l => ({
    employee: employees.find(e => e.id === l.employee_id)?.name || "غير معروف",
    type: l.leave_type,
    from: l.start_date,
    to: l.end_date,
    days: l.days,
    reason: l.reason,
  }));
  const reportSources = [
    {
      label: "مسيرات الرواتب", mode: "monthly", dateField: "disbursementDate", rows: disbursements,
      columns: [
        { key: "disbursementDate", label: "تاريخ الصرف" },
        { key: "totalAmount", label: "الإجمالي", format: money },
        { key: "paymentMethod", label: "طريقة الصرف" },
      ],
    },
    {
      label: "الإجازات", mode: "monthly", dateField: "from", rows: leaveReportRows,
      columns: [
        { key: "employee", label: "الموظف" },
        { key: "type", label: "النوع" },
        { key: "from", label: "من" },
        { key: "to", label: "إلى" },
        { key: "days", label: "الأيام" },
        { key: "reason", label: "السبب" },
      ],
    },
    { label: "الموظفون", mode: "cumulative", rows: employees },
  ];

  return (
    <div>
      <ModuleHeader icon={Briefcase} title="الموارد البشرية" subtitle="إدارة الموظفين والهيكل التنظيمي والإجازات والرواتب">
        <MonthlyReportButton moduleTitle="الموارد البشرية" sources={reportSources} />
      </ModuleHeader>

      {perms.canEdit && (
        <div style={hq.row}>
          <button style={hq.btn("#059669")} onClick={() => fireQuick("leaves", "leave")}>
            <CalendarDays size={16} /><span>طلب إجازة / إذن</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "employees" && <EmployeesTab canEdit={perms.canEdit} employees={employees} setEmployees={setEmployees} loading={loadingEmp} />}
      {tab === "structure" && <StructureTab />}
      {tab === "leaves" && <LeavesTab canEdit={perms.canEdit} triggerOpen={triggers.leave} leaves={leaves} setLeaves={setLeaves} loading={loadingLeaves} employees={employees} />}
      {tab === "requests" && <RequestsTab user={user} perms={perms} scope="all" />}
      {tab === "payroll" && <PayrollTab employees={employees} disbursements={disbursements} setDisbursements={setDisbursements} />}
      {tab === "meetings" && <MeetingsTab departmentName="الموارد البشرية" module="hr" />}
      {tab === "transactions" && <TransactionsTab budget={1200000} module="hr" />}
      {tab === "plan" && <OperationalPlanTab departmentKey="hr" />}
      {tab === "reports" && <ReportsTab departmentName="الموارد البشرية" />}
    </div>
  );
}

const hq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: (color) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 16px", borderRadius: 10,
    background: `${color}10`, color, border: `1px solid ${color}30`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 700,
    cursor: "pointer", transition: "all 0.15s",
  }),
};

/* ─────────────────────────────────────────────
   ✦ Employees Tab
   ───────────────────────────────────────────── */
const INITIAL_EMP = { name: "", empId: "", position: "", department: "", phone: "", email: "", salary: "", qualification: "", nationality: "سعودي", leaveBalance: 30, joinDate: "" };

function EmployeesTab({ canEdit, employees, setEmployees, loading }) {
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // الموظف قيد التعديل أو null (إضافة)
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_EMP);
  const [showToast, ToastView] = useToast();

  const [custody, setCustody] = useState([]);
  useEffect(() => { getAssetsCustody().then(({ data }) => setCustody((data || []).map(mapCustodyRow))).catch(() => {}); }, []);

  function openAdd() { setEditing(null); setForm(INITIAL_EMP); setShowAdd(true); }
  function openEdit(emp) {
    setEditing(emp);
    setForm({
      name: emp.name || "", empId: emp.empId || "", position: emp.position || "",
      department: emp.department || "", phone: emp.phone || "", email: emp.email || "",
      salary: emp.salary || "", qualification: emp.qualification || "", nationality: emp.nationality || "سعودي", leaveBalance: emp.leaveBalance ?? 30, joinDate: emp.joinDate || "",
    });
    setSelected(null);
    setShowAdd(true);
  }

  async function handleSave() {
    if (!form.name || !form.empId || !form.position || !form.department || !form.joinDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const payload = {
      emp_id: form.empId,
      name: form.name,
      position: form.position,
      department: form.department,
      phone: form.phone || null,
      email: form.email || null,
      join_date: form.joinDate,
      salary: Number(form.salary) || 0,
      qualification: form.qualification || null,
      nationality: form.nationality || null,
      leave_balance: Number(form.leaveBalance) || 30,
    };
    setSaving(true);
    try {
      if (editing) {
        const up = await updateEmployee(editing.id, payload);
        const mapped = mapEmployeeRow(up);
        setEmployees(prev => prev.map(e => (e.id === editing.id ? mapped : e)));
        showToast("تم تحديث بيانات الموظف");
      } else {
        const created = await createEmployee(payload);
        setEmployees(prev => [mapEmployeeRow(created), ...prev]);
        showToast("تمت إضافة الموظف");
      }
      setForm(INITIAL_EMP);
      setEditing(null);
      setShowAdd(false);
    } catch (e) {
      showToast(e.message || "تعذّر الحفظ", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(emp) {
    if (!window.confirm(`حذف الموظف "${emp.name}"؟`)) return;
    try {
      await deleteEmployee(emp.id);
      setEmployees(prev => prev.filter(e => e.id !== emp.id));
      setSelected(null);
      showToast("تم حذف الموظف");
    } catch (e) {
      showToast(e.message || "تعذّر الحذف", "error");
    }
  }

  const depts = [...new Set(employees.map(e => e.department))];
  const EMP_CSV_COLUMNS = [
    { key: "empId",        header: "الرقم الوظيفي" },
    { key: "name",         header: "الاسم" },
    { key: "position",     header: "المسمى الوظيفي" },
    { key: "department",   header: "القسم" },
    { key: "phone",        header: "الهاتف" },
    { key: "email",        header: "البريد الإلكتروني" },
    { key: "joinDate",     header: "تاريخ التعيين" },
    { key: "salary",       header: "الراتب" },
    { key: "qualification",header: "المؤهل" },
    { key: "nationality",  header: "الجنسية" },
    { key: "leaveBalance", header: "رصيد الإجازات" },
  ];

  function handleExport() {
    exportToCSV(filtered, EMP_CSV_COLUMNS, "الموظفون");
    showToast(`تم تصدير ${filtered.length} موظف`);
  }

  // الاستيراد يحفظ صفًا صفًا ويتوقف عند أول خطأ فعلي من القاعدة
  async function handleImport(rows) {
    let ok = 0;
    const added = [];
    for (const r of rows) {
      if (!r.name || !r.empId) continue;
      try {
        const created = await createEmployee({
          emp_id: r.empId,
          name: r.name,
          position: r.position || "",
          department: r.department || "",
          phone: r.phone || null,
          email: r.email || null,
          join_date: r.joinDate || null,
          salary: Number(r.salary) || 0,
          qualification: r.qualification || null,
          nationality: r.nationality || null,
          leave_balance: Number(r.leaveBalance) || 30,
        });
        ok++;
        if (created) added.push(mapEmployeeRow(created));
      } catch (e) {
        showToast(`توقف الاستيراد عند "${r.name}": ${e?.message || "خطأ"}`, "error");
        break;
      }
    }
    if (added.length) setEmployees(prev => [...added, ...prev]);
    setShowImport(false);
    if (ok) showToast(`تم استيراد ${ok} موظف`);
  }

  const filtered = employees.filter(e => {
    const matchSearch = e.name.includes(search) || e.position.includes(search) || e.empId.includes(search);
    const matchDept = filterDept === "all" || e.department === filterDept;
    return matchSearch && matchDept;
  });

  const EMP_STATUSES = [
    { key: "active", label: "فعّال", color: C.success },
    { key: "on_leave", label: "في إجازة", color: C.warning },
    { key: "resigned", label: "مستقيل", color: C.danger },
    { key: "terminated", label: "منتهي", color: C.danger },
    { key: "ended_service", label: "منتهية خدمته", color: C.danger },
  ];

  const TERMINATION_STATUSES = ["resigned", "terminated", "ended_service"];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الموظفين" value={employees.length} icon={Users} color="#0284c7" />
        <KPICard title="فعّالين" value={employees.filter(e => e.status === "active").length} icon={UserCheck} color={C.success} />
        <KPICard title="في إجازة" value={employees.filter(e => e.status === "on_leave").length} icon={TreePine} color={C.warning} />
        <KPICard title="الأقسام" value={depts.length} icon={Building} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الرقم الوظيفي..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأقسام</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <Btn icon={Download} variant="ghost" size="sm" onClick={handleExport}>تصدير</Btn>
            {canEdit && <Btn icon={Upload} variant="ghost" size="sm" onClick={() => setShowImport(true)}>استيراد</Btn>}
            {canEdit && <Btn icon={Plus} onClick={openAdd} size="sm">إضافة موظف</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 14 }}>جارٍ تحميل الموظفين…</div>
        ) : employees.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 14 }}>لا يوجد موظفون مسجّلون بعد.</div>
        ) : (
          <DataTable
            columns={[
              { key: "empId", label: "الرقم", width: "80px", render: v => <span style={{ fontWeight: 600, color: C.primary, fontFamily: "monospace" }}>{v}</span> },
              { key: "name", label: "الاسم", render: (v, row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={styles.tableAvatar}>{v.charAt(0)}</div>
                  <div>
                    <span style={{ fontWeight: 600, display: "block" }}>{v}</span>
                    <span style={{ fontSize: 11, color: C.textLight }}>{row.position}</span>
                  </div>
                </div>
              )},
              { key: "department", label: "القسم", width: "120px" },
              { key: "joinDate", label: "تاريخ التعيين", width: "100px" },
              { key: "salary", label: "الراتب", width: "100px", render: v => `${v.toLocaleString()} ر` },
              { key: "phone", label: "الهاتف", width: "110px" },
              { key: "status", label: "الحالة", width: "90px", render: v => <StatusBadge status={v} statuses={EMP_STATUSES} /> },
            ]}
            data={filtered}
            onRowClick={row => setSelected(row)}
          />
        )}
      </Card>

      {selected && (() => {
        const openN = openCustodyCount(selected.id, custody);
        const isTerminating = TERMINATION_STATUSES.includes(selected.status);
        return (
        <Modal title={`موظف — ${selected.name}`} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {isTerminating && openN > 0 && (
              <div style={{
                background: `${C.danger}10`, border: `1.5px solid ${C.danger}40`,
                borderRight: `4px solid ${C.danger}`, borderRadius: 10,
                padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
              }}>
                <AlertTriangle size={22} color={C.danger} />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.danger }}>
                    يوجد على الموظف {openN} عهد يجب استلامها قبل إغلاق ملفه
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>
                    لا يمكن إنهاء إجراءات الإغلاق قبل تسوية جميع العهد المُسلّمة.
                  </p>
                </div>
                <Btn variant="ghost" size="sm" icon={Package}
                  onClick={() => window.alert(
                    custody
                      .filter(c => c.employeeId === selected.id && c.status !== "returned")
                      .map(c => `• ${c.assetName} — تسليم: ${c.handoverDate} — حالة: ${c.status}`)
                      .join("\n")
                  )}
                >عرض العهد</Btn>
              </div>
            )}
            <div style={styles.empHeader}>
              <div style={styles.empAvatarLg}>{selected.name.charAt(0)}</div>
              <div>
                <h3 style={{ margin: 0, color: C.primary }}>{selected.name}</h3>
                <p style={{ margin: "2px 0", color: C.teal, fontWeight: 600, fontSize: 14 }}>{selected.position}</p>
                <p style={{ margin: 0, color: C.textLight, fontSize: 12 }}>{selected.department} — {selected.empId}</p>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
              <div style={styles.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>
              <div style={styles.detailRow}><strong>تاريخ التعيين:</strong> {selected.joinDate}</div>
              <div style={styles.detailRow}><strong>الراتب:</strong> {selected.salary.toLocaleString()} ريال</div>
              <div style={styles.detailRow}><strong>المؤهل:</strong> {selected.qualification}</div>
              <div style={styles.detailRow}><strong>الجنسية:</strong> {selected.nationality}</div>
              <div style={styles.detailRow}><strong>رصيد الإجازات:</strong> {selected.leaveBalance} يوم</div>
              <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={EMP_STATUSES} /></div>
            </div>
            {canEdit && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                <Btn size="sm" onClick={() => openEdit(selected)}>تعديل</Btn>
                <Btn size="sm" variant="ghost" onClick={() => handleDelete(selected)}>حذف</Btn>
              </div>
            )}
          </div>
        </Modal>
        );
      })()}

      {showImport && (
        <ImportModal
          title="استيراد الموظفين"
          entityLabel="موظف"
          columns={EMP_CSV_COLUMNS}
          onImport={handleImport}
          onClose={() => setShowImport(false)}
        />
      )}

      {showAdd && (
        <Modal title={editing ? "تعديل بيانات الموظف" : "إضافة موظف جديد"} onClose={() => { setShowAdd(false); setEditing(null); setForm(INITIAL_EMP); }} width={650}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الاسم الكامل" required placeholder="الاسم" value={form.name} onChange={v => setForm({ ...form, name: v })} />
              <Field label="الرقم الوظيفي" required placeholder="EMP-XXX" value={form.empId} onChange={v => setForm({ ...form, empId: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المنصب" required placeholder="المسمى الوظيفي" value={form.position} onChange={v => setForm({ ...form, position: v })} />
              <Field label="القسم" type="select" options={["الإدارة التنفيذية","الجاليات","المساجد","النسائي","المالية","تنمية الموارد","الاتصال","التطوع","الموارد البشرية"]} required value={form.department} onChange={v => setForm({ ...form, department: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="البريد" placeholder="emp@bnr.org.sa" value={form.email} onChange={v => setForm({ ...form, email: v })} />
              <Field label="الراتب" type="number" placeholder="0" value={form.salary} onChange={v => setForm({ ...form, salary: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الجنسية" placeholder="سعودي" value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="رصيد الإجازات" type="number" placeholder="30" value={form.leaveBalance} onChange={v => setForm({ ...form, leaveBalance: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المؤهل" type="select" options={["ثانوية","دبلوم","بكالوريوس","ماجستير","دكتوراه"]} value={form.qualification} onChange={v => setForm({ ...form, qualification: v })} />
              <Field label="تاريخ التعيين" type="date" required value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave} disabled={saving}>{saving ? "جارٍ الحفظ…" : "حفظ"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setEditing(null); setForm(INITIAL_EMP); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Structure Tab — الهيكل التنظيمي
   ───────────────────────────────────────────── */
function StructureTab() {
  const departments = [
    { name: "الاتصال المؤسسي", color: "#c026d3", subs: ["العلاقات العامة", "الإعلام والنشر", "الشركات والرعايات"] },
    { name: "إدارة البرامج والمشاريع", color: "#0891b2", subs: ["قسم الدعوة", "قسم الجاليات", "القسم النسائي", "قسم المشاريع"] },
    { name: "الإدارة المالية", color: "#16a34a", subs: ["المدير المالي", "المحاسب"] },
    { name: "تنمية الموارد والتسويق", color: "#d97706", subs: ["التسويق", "إدارة المتبرعين", "تنمية الموارد"] },
    { name: "الأوقاف والأملاك", color: "#7c3aed", subs: ["إدارة الإيجارات", "متابعة السداد والعقود"] },
  ];
  const supportDepts = [
    { name: "الحوكمة والامتثال", color: "#6366f1", subs: ["الحوكمة", "العمل المؤسسي", "مراجع الامتثال الداخلي"] },
    { name: "التقنية والتحول الرقمي", color: "#475569", subs: ["الموقع والمتجر", "الأنظمة", "الدعم الفني"] },
  ];
  const nb = { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 20px", textAlign: "center", minWidth: 140, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" };
  const vl = { width: 2, height: 20, background: C.border, margin: "0 auto" };
  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 28, overflowX: "auto" }}>
        <h4 style={styles.sectionTitle}><Layers size={18} color="#7c3aed" /> الهيكل التنظيمي للجمعية</h4>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, minWidth: 880 }}>
          <div style={{ ...nb, borderTop: "4px solid #1e3a5f", padding: "16px 40px", background: "#1e3a5f06" }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#1e3a5f" }}>الجمعية العمومية</p>
          </div>
          <div style={vl} />
          <div style={{ ...nb, borderTop: "4px solid #1e3a5f", padding: "16px 40px" }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e3a5f" }}>مجلس الإدارة</p>
          </div>
          <div style={vl} />
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ ...nb, borderTop: `4px solid ${C.primary}`, padding: "16px 36px" }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${C.primary}12`, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}><Users size={18} color={C.primary} /></div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.primary }}>المدير التنفيذي</p>
            </div>
            <div style={{ height: 2, background: C.border, width: 40 }} />
            <div style={{ ...nb, borderTop: "4px solid #64748b", padding: "12px 24px" }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#64748b" }}>مدير مكتب</p>
            </div>
          </div>
          <div style={vl} />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", width: "100%" }}>
            {departments.map((dept, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "1 1 160px", maxWidth: 200 }}>
                <div style={{ ...nb, borderTop: `4px solid ${dept.color}`, width: "100%", padding: "12px 10px", background: `${dept.color}05` }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: dept.color, lineHeight: 1.5 }}>{dept.name}</p>
                </div>
                {dept.subs.map((sub, j) => (
                  <div key={j} style={{ width: "100%" }}>
                    <div style={{ width: 2, height: 8, background: `${dept.color}40`, margin: "0 auto" }} />
                    <div style={{ padding: "8px 10px", borderRadius: 8, background: `${dept.color}08`, border: `1px solid ${dept.color}20`, textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: dept.color }}>{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ height: 24 }} />
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center", width: "100%" }}>
            {supportDepts.map((dept, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "1 1 200px", maxWidth: 240 }}>
                <div style={{ ...nb, borderTop: `4px solid ${dept.color}`, width: "100%", padding: "12px 14px", background: `${dept.color}05` }}>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: dept.color, lineHeight: 1.5 }}>{dept.name}</p>
                </div>
                {dept.subs.map((sub, j) => (
                  <div key={j} style={{ width: "100%" }}>
                    <div style={{ width: 2, height: 8, background: `${dept.color}40`, margin: "0 auto" }} />
                    <div style={{ padding: "8px 10px", borderRadius: 8, background: `${dept.color}08`, border: `1px solid ${dept.color}20`, textAlign: "center" }}>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: dept.color }}>{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Leaves Tab — الإجازات
   ───────────────────────────────────────────── */
const LEAVE_TYPES = ["سنوية", "مرضية", "اضطرارية", "بدون راتب", "إذن خروج", "إذن تأخر"];
const INITIAL_LEAVE = {
  employee: "", type: "سنوية",
  from: "", to: "", fromHour: "", toHour: "",
  reason: "", replacementYes: "لا", replacementName: "",
  medicalAttachment: "", notes: "",
};

const lvActBtn = (color) => ({
  padding: "3px 10px", borderRadius: 6, border: `1px solid ${color}40`,
  background: `${color}10`, color, fontSize: 11, fontWeight: 700,
  fontFamily: "'Readex Pro', sans-serif", cursor: "pointer",
});

function LeavesTab({ canEdit, triggerOpen = 0, leaves, setLeaves, loading, employees }) {
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_LEAVE);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const isPermission = form.type === "إذن خروج" || form.type === "إذن تأخر";
  const isSick = form.type === "مرضية";

  const days = (form.from && form.to)
    ? Math.max(1, Math.round((new Date(form.to) - new Date(form.from)) / (1000 * 60 * 60 * 24)) + 1)
    : 0;

  async function handleSave() {
    if (!form.employee || !form.type || !form.from || !form.reason) {
      showToast("يرجى تعبئة الحقول المطلوبة (الموظف، النوع، تاريخ البداية، السبب)", "error");
      return;
    }
    if (!isPermission && !form.to) {
      showToast("تاريخ النهاية مطلوب", "error");
      return;
    }
    if (form.replacementYes === "نعم" && !form.replacementName) {
      showToast("يرجى تحديد اسم البديل", "error");
      return;
    }
    const emp = employees.find(e => e.name === form.employee);
    if (!emp) {
      showToast("تعذّر ربط الموظف — أعد اختياره", "error");
      return;
    }
    const calcDays = isPermission ? 0 : days;
    const payload = {
      employee_id: emp.id,
      leave_type: form.type,
      start_date: form.from,
      end_date: form.to || form.from,
      from_hour: form.fromHour || null,
      to_hour: form.toHour || null,
      days: calcDays,
      reason: form.reason || null,
      replacement_name: form.replacementYes === "نعم" ? form.replacementName : null,
      medical_attachment: form.medicalAttachment || null,
      notes: form.notes || null,
      status: "pending",
    };
    setSaving(true);
    try {
      const created = await createLeave(payload);
      setLeaves(prev => [created, ...prev]);
      setForm(INITIAL_LEAVE);
      setShowAdd(false);
      showToast("تم إرسال طلب الإجازة / الإذن");
    } catch (e) {
      showToast(e.message || "تعذّر الإرسال", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatus(id, status) {
    try {
      const up = await updateLeave(id, { status });
      setLeaves(prev => prev.map(l => (l.id === id ? up : l)));
      showToast("تم تحديث حالة الطلب");
    } catch (e) {
      showToast(e.message || "تعذّر التحديث", "error");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("حذف هذا الطلب؟")) return;
    try {
      await deleteLeave(id);
      setLeaves(prev => prev.filter(l => l.id !== id));
      showToast("تم حذف الطلب");
    } catch (e) {
      showToast(e.message || "تعذّر الحذف", "error");
    }
  }

  const LEAVE_STATUSES = [
    { key: "approved", label: "معتمدة", color: C.success },
    { key: "pending", label: "بانتظار الموافقة", color: C.warning },
    { key: "rejected", label: "مرفوضة", color: C.danger },
    { key: "active", label: "جارية", color: C.teal },
  ];

  // ✦ تحويل الصفوف الحيّة (snake_case) لشكل الأعمدة + استرجاع اسم الموظف من employees state
  const rows = leaves.map(l => ({
    id: l.id,
    employee: employees.find(e => e.id === l.employee_id)?.name || "غير معروف",
    type: l.leave_type,
    from: l.start_date,
    to: l.end_date,
    days: l.days,
    reason: l.reason,
    status: l.status,
  }));
  const filtered = rows.filter(l => filterStatus === "all" || l.status === filterStatus);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="طلبات الإجازة" value={leaves.length} icon={TreePine} color="#059669" />
        <KPICard title="بانتظار الموافقة" value={leaves.filter(l => l.status === "pending").length} icon={Clock} color={C.warning} />
        <KPICard title="معتمدة" value={leaves.filter(l => l.status === "approved").length} icon={CheckCircle} color={C.success} />
        <KPICard title="جارية حالياً" value={leaves.filter(l => l.status === "active").length} icon={CalendarOff} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الحالات</option>
            {LEAVE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">طلب إجازة</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 14 }}>جارٍ تحميل الإجازات…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 14 }}>لا توجد طلبات إجازة بعد.</div>
        ) : (
          <DataTable
            columns={[
              { key: "employee", label: "الموظف", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
              { key: "type", label: "النوع", width: "100px" },
              { key: "from", label: "من", width: "100px" },
              { key: "to", label: "إلى", width: "100px" },
              { key: "days", label: "الأيام", width: "70px", render: v => <span style={{ fontWeight: 700, color: C.primary }}>{v}</span> },
              { key: "reason", label: "السبب" },
              { key: "status", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={LEAVE_STATUSES} /> },
              ...(canEdit ? [{ key: "actions", label: "إجراءات", width: "160px", render: (v, row) => (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {row.status === "pending" && (
                    <>
                      <button style={lvActBtn(C.success)} onClick={() => handleStatus(row.id, "approved")}>اعتماد</button>
                      <button style={lvActBtn(C.danger)} onClick={() => handleStatus(row.id, "rejected")}>رفض</button>
                    </>
                  )}
                  <button style={lvActBtn(C.textLight)} onClick={() => handleDelete(row.id)}>حذف</button>
                </div>
              ) }] : []),
            ]}
            data={filtered}
          />
        )}
      </Card>

      {showAdd && (
        <Modal title="طلب إجازة / إذن" onClose={() => { setShowAdd(false); setForm(INITIAL_LEAVE); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الموظف" type="select" options={employees.map(e => e.name)} required value={form.employee} onChange={v => setForm({ ...form, employee: v })} />
              <Field label="نوع الطلب" type="select" options={LEAVE_TYPES} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="من تاريخ" type="date" required value={form.from} onChange={v => setForm({ ...form, from: v })} />
              <Field label={isPermission ? "إلى تاريخ" : "إلى تاريخ"} type="date" required={!isPermission} value={form.to} onChange={v => setForm({ ...form, to: v })} />
              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>عدد الأيام</label>
                <div style={{ padding: "10px 12px", background: C.bg, borderRadius: 8, fontWeight: 700, color: C.primary, textAlign: "center" }}>
                  {isPermission ? "—" : (days || 0)} يوم
                </div>
              </div>
            </div>

            {isPermission && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="من ساعة" type="time" value={form.fromHour} onChange={v => setForm({ ...form, fromHour: v })} />
                <Field label="إلى ساعة" type="time" value={form.toHour} onChange={v => setForm({ ...form, toHour: v })} />
              </div>
            )}

            <Field label="السبب" required type="textarea" placeholder="السبب التفصيلي للطلب..." rows={3} value={form.reason} onChange={v => setForm({ ...form, reason: v })} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              <Field label="هل يوجد بديل؟" type="select" options={["لا", "نعم"]} value={form.replacementYes} onChange={v => setForm({ ...form, replacementYes: v })} />
              {form.replacementYes === "نعم" && (
                <Field label="اسم البديل" required type="select" options={employees.map(e => e.name).filter(n => n !== form.employee)} value={form.replacementName} onChange={v => setForm({ ...form, replacementName: v })} />
              )}
            </div>

            {isSick && (
              <Field label="رفع تقرير طبي (رابط/اسم الملف)" placeholder="medical_report.pdf أو رابط الملف" value={form.medicalAttachment} onChange={v => setForm({ ...form, medicalAttachment: v })} />
            )}

            <Field label="ملاحظات إضافية" type="textarea" placeholder="ملاحظات اختيارية..." rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={TreePine} onClick={handleSave} disabled={saving}>{saving ? "جارٍ الإرسال…" : "إرسال الطلب"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_LEAVE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Payroll Tab — الرواتب
   ───────────────────────────────────────────── */
const PAYROLL_MONTHS = ["محرم", "صفر", "ربيع الأول", "ربيع الثاني", "جمادى الأولى", "جمادى الثانية", "رجب", "شعبان", "رمضان", "شوال", "ذو القعدة", "ذو الحجة"];
const PAYROLL_PAYMENT_METHODS = ["تحويل بنكي", "نقدي", "شيك"];
const INITIAL_PAYROLL = { month: "", year: "1447", totalAmount: "", paymentMethod: "تحويل بنكي", disbursementDate: "", notes: "" };

const INITIAL_ALLOWANCE = { employee: "", type: "بدل سكن", amount: "", month: "", notes: "" };
const ALLOWANCE_TYPES = ["بدل سكن", "بدل نقل", "بدل اتصال", "بدل طبيعة عمل", "مكافأة استثنائية", "بدل أخرى"];

function mapAllowanceRow(r) {
  return {
    id: r.id,
    employee: r.employee_name || "",
    type: r.allowance_type || "",
    amount: Number(r.amount) || 0,
    month: r.month || "",
    notes: r.notes || "",
  };
}

function PayrollTab({ employees, disbursements, setDisbursements }) {
  const totalSalary = employees.reduce((s, e) => s + (e.salary || 0), 0);
  const empCount = employees.length;
  const [allowances, setAllowances] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [savingDisb, setSavingDisb] = useState(false);
  const [showAllowance, setShowAllowance] = useState(false);
  const [form, setForm] = useState(INITIAL_PAYROLL);
  const [allowanceForm, setAllowanceForm] = useState(INITIAL_ALLOWANCE);
  const [showToast, ToastView] = useToast();

  useEffect(() => {
    getAllowances()
      .then(rows => setAllowances((rows || []).map(mapAllowanceRow)))
      .catch(() => {});
  }, []);

  async function handleSaveDisbursement() {
    if (!form.month || !form.totalAmount) {
      showToast("الشهر والمبلغ مطلوبان", "error");
      return;
    }
    // ✦ يُخزَّن كمعاملة hr/expense؛ الشهر/السنة الهجريان في description ("رواتب <شهر> <سنة>")
    const payload = {
      module: "hr",
      direction: "expense",
      txn_date: form.disbursementDate || null,
      amount: Number(form.totalAmount) || 0,
      payment_method: form.paymentMethod,
      description: `رواتب ${form.month} ${form.year}`,
      txn_type: "رواتب",
      status: "paid",
      notes: form.notes || null,
    };
    setSavingDisb(true);
    try {
      const created = await createTransaction(payload);
      setDisbursements(prev => [mapTxnToDisbursement(created), ...prev]);
      setForm(INITIAL_PAYROLL);
      setShowAdd(false);
      showToast("تم تسجيل صرف الرواتب");
    } catch (e) {
      showToast(e.message || "تعذّر التسجيل", "error");
    } finally {
      setSavingDisb(false);
    }
  }

  async function handleSaveAllowance() {
    if (!allowanceForm.employee || !allowanceForm.amount) {
      showToast("الموظف والمبلغ مطلوبان", "error");
      return;
    }
    const payload = {
      employee_name: allowanceForm.employee,
      allowance_type: allowanceForm.type,
      amount: Number(allowanceForm.amount) || 0,
      month: allowanceForm.month,
      notes: allowanceForm.notes,
    };
    try {
      const created = await createAllowance(payload);
      setAllowances(prev => [mapAllowanceRow(created), ...prev]);
      setAllowanceForm(INITIAL_ALLOWANCE);
      setShowAllowance(false);
      showToast("تمت إضافة البدل");
    } catch (e) {
      showToast(e?.message || "تعذّر إضافة البدل", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الرواتب الشهرية" value={`${(totalSalary / 1000).toFixed(0)}K`} icon={DollarSign} color={C.primary} />
        <KPICard title="عدد الموظفين" value={empCount} icon={Users} color="#0284c7" />
        <KPICard title="متوسط الراتب" value={`${(totalSalary / (empCount || 1) / 1000).toFixed(1)}K`} icon={TrendingUp} color={C.teal} />
        <KPICard title="الرواتب السنوية" value={`${(totalSalary * 12 / 1000000).toFixed(1)}M`} icon={Calendar} color={C.accent} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
        <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">صرف راتب شهري</Btn>
        <Btn icon={Plus} variant="ghost" onClick={() => setShowAllowance(true)} size="sm">إضافة بدل</Btn>
      </div>

      {/* Disbursements history */}
      {disbursements.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={styles.sectionTitle}>سجل صرف الرواتب</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {disbursements.map(d => (
              <div key={d.id} style={{ padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>{d.month} {d.year}</p>
                <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 700, color: C.success }}>{d.totalAmount.toLocaleString()} ر.س</p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: C.textLight }}>{d.paymentMethod} — {d.disbursementDate}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Allowances list */}
      {allowances.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={styles.sectionTitle}>البدلات المضافة</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
            {allowances.map(a => (
              <div key={a.id} style={{ padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>{a.employee}</p>
                <p style={{ margin: "2px 0", fontSize: 12, color: C.textLight }}>{a.type} — {a.month}</p>
                <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 700, color: C.accent }}>{a.amount.toLocaleString()} ر.س</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "empId", label: "الرقم", width: "80px", render: v => <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{v}</span> },
            { key: "name", label: "الموظف", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "department", label: "القسم", width: "120px" },
            { key: "position", label: "المنصب", width: "140px" },
            { key: "salary", label: "الراتب الأساسي", width: "110px", render: v => `${v.toLocaleString()}` },
            { key: "allowances", label: "البدلات", width: "90px", render: (v, row) => `${Math.round(row.salary * 0.25).toLocaleString()}` },
            { key: "total", label: "الإجمالي", width: "110px", render: (v, row) => <span style={{ fontWeight: 700, color: C.success }}>{Math.round(row.salary * 1.25).toLocaleString()}</span> },
          ]}
          data={employees.filter(e => e.status === "active")}
        />
      </Card>

      <div style={styles.totalBar}>
        <span>إجمالي الرواتب الشهرية (مع البدلات):</span>
        <span style={{ fontWeight: 700, color: C.primary, fontSize: 18 }}>{Math.round(totalSalary * 1.25).toLocaleString()} ريال</span>
      </div>

      {/* Department breakdown */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Building size={18} color="#7c3aed" /> توزيع الرواتب حسب القسم</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {DEPT_PAYROLL.map((d, i) => (
            <div key={i} style={styles.payrollRow}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: 160 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{d.name}</span>
              </div>
              <span style={{ fontSize: 13, color: C.textLight }}>{d.count} موظف</span>
              <div style={{ flex: 1 }}>
                <div style={styles.payrollBarBg}>
                  <div style={{ height: "100%", borderRadius: 4, width: `${(d.total / 35000) * 100}%`, background: d.color }} />
                </div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.primary, minWidth: 90, textAlign: "left" }}>{d.total.toLocaleString()} ر</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Disbursement modal */}
      {showAdd && (
        <Modal title="صرف الرواتب الشهرية" onClose={() => { setShowAdd(false); setForm(INITIAL_PAYROLL); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الشهر الهجري" type="select" options={PAYROLL_MONTHS} required value={form.month} onChange={v => setForm({ ...form, month: v })} />
              <Field label="السنة الهجرية" placeholder="1447" value={form.year} onChange={v => setForm({ ...form, year: v })} />
            </div>
            <Field label="المبلغ الإجمالي (ر.س)" type="number" required placeholder="0" value={form.totalAmount} onChange={v => setForm({ ...form, totalAmount: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="طريقة الدفع" type="select" options={PAYROLL_PAYMENT_METHODS} value={form.paymentMethod} onChange={v => setForm({ ...form, paymentMethod: v })} />
              <Field label="تاريخ الصرف" type="date" value={form.disbursementDate} onChange={v => setForm({ ...form, disbursementDate: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={DollarSign} onClick={handleSaveDisbursement} disabled={savingDisb}>{savingDisb ? "جارٍ التسجيل…" : "تأكيد الصرف"}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PAYROLL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Allowance modal */}
      {showAllowance && (
        <Modal title="إضافة بدل" onClose={() => { setShowAllowance(false); setAllowanceForm(INITIAL_ALLOWANCE); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الموظف" type="select" options={employees.map(e => e.name)} required value={allowanceForm.employee} onChange={v => setAllowanceForm({ ...allowanceForm, employee: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="نوع البدل" type="select" options={ALLOWANCE_TYPES} value={allowanceForm.type} onChange={v => setAllowanceForm({ ...allowanceForm, type: v })} />
              <Field label="المبلغ (ر.س)" type="number" required placeholder="0" value={allowanceForm.amount} onChange={v => setAllowanceForm({ ...allowanceForm, amount: v })} />
              <Field label="الشهر" type="select" options={PAYROLL_MONTHS} value={allowanceForm.month} onChange={v => setAllowanceForm({ ...allowanceForm, month: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} placeholder="سبب البدل..." value={allowanceForm.notes} onChange={v => setAllowanceForm({ ...allowanceForm, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={DollarSign} onClick={handleSaveAllowance}>حفظ البدل</Btn>
              <Btn variant="ghost" onClick={() => { setShowAllowance(false); setAllowanceForm(INITIAL_ALLOWANCE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Static Data
   ═════════════════════════════════════════════ */
const DEPT_PAYROLL = [];
/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },
  totalBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", marginTop: 12, background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 500, color: C.primary },

  tableAvatar: {
    width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0,
  },
  empHeader: { display: "flex", alignItems: "center", gap: 20, paddingBottom: 14, borderBottom: `1px solid ${C.border}` },
  empAvatarLg: {
    width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22, flexShrink: 0,
  },

  /* Org Chart */
  orgChart: { display: "flex", flexDirection: "column", alignItems: "center", gap: 0, marginTop: 16 },
  orgLevel: { display: "flex", gap: 12, justifyContent: "center" },
  orgLine: { width: 2, height: 24, background: C.border, margin: "0 auto" },
  orgNode: {
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px",
    textAlign: "center", minWidth: 130, boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  orgNodeIcon: { width: 32, height: 32, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 6 },

  /* Payroll */
  payrollRow: { display: "flex", alignItems: "center", gap: 20 },
  payrollBarBg: { height: 8, borderRadius: 4, background: C.bg, overflow: "hidden" },
};
